import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertCircle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useSEO";

interface MetaTag {
  name: string;
  content: string;
  status: 'success' | 'warning' | 'error';
}

interface RouteCheck {
  path: string;
  title: string;
  canonical: string;
  metaTags: MetaTag[];
}

const SEOHealthCheck = () => {
  useSEO({
    title: 'SEO Health Check - Ommni Admin',
    description: 'Admin access only',
    noindex: true,
  });
  
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [sitemapStatus, setSitemapStatus] = useState<'checking' | 'success' | 'error'>('checking');
  const [sitemapContent, setSitemapContent] = useState<string>('');
  const [routeChecks, setRouteChecks] = useState<RouteCheck[]>([]);
  const primaryDomain = 'https://ommni.io';

  useEffect(() => {
    checkSitemap();
    checkRoutes();
  }, []);

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate("/auth");
    }
  }, [isAdmin, adminLoading, navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const checkSitemap = async () => {
    try {
      const response = await fetch('/sitemap.xml');
      const text = await response.text();
      setSitemapContent(text);
      
      // Check if all URLs use the primary domain
      const hasPrimaryDomain = text.includes(primaryDomain);
      const hasWrongDomain = text.includes('www.ommni.io') || text.includes('http://');
      
      if (hasPrimaryDomain && !hasWrongDomain) {
        setSitemapStatus('success');
      } else {
        setSitemapStatus('error');
      }
    } catch (error) {
      setSitemapStatus('error');
    }
  };

  const checkRoutes = () => {
    // Define critical routes to check
    const routes = [
      { path: '/', name: 'Home' },
      { path: '/pricing', name: 'Pricing' },
      { path: '/features', name: 'Features' },
      { path: '/integrations', name: 'Integrations' },
      { path: '/blog', name: 'Blog' },
    ];

    const checks: RouteCheck[] = routes.map(route => {
      // Simulate checking meta tags (in a real implementation, you'd fetch each page)
      const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href') || '';
      const description = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
      const ogTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content') || '';
      const ogUrl = document.querySelector('meta[property="og:url"]')?.getAttribute('content') || '';

      const metaTags: MetaTag[] = [
        {
          name: 'Canonical URL',
          content: canonical,
          status: canonical.startsWith(primaryDomain) ? 'success' : 'error'
        },
        {
          name: 'Meta Description',
          content: description,
          status: description.length > 50 && description.length < 160 ? 'success' : 'warning'
        },
        {
          name: 'OG:Title',
          content: ogTitle,
          status: ogTitle.length > 0 ? 'success' : 'error'
        },
        {
          name: 'OG:URL',
          content: ogUrl,
          status: ogUrl.startsWith(primaryDomain) ? 'success' : 'error'
        },
      ];

      return {
        path: route.path,
        title: route.name,
        canonical,
        metaTags
      };
    });

    setRouteChecks(checks);
  };

  const getStatusIcon = (status: 'success' | 'warning' | 'error' | 'checking') => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: 'success' | 'warning' | 'error') => {
    const variants = {
      success: 'default',
      warning: 'secondary',
      error: 'destructive'
    };
    return (
      <Badge variant={variants[status] as any}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="mb-8 flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold mb-4">SEO Health Check</h1>
              <p className="text-muted-foreground">
                Verify that all canonical URLs, meta tags, and sitemaps are using the primary domain: <strong>{primaryDomain}</strong>
              </p>
            </div>
            <Button onClick={handleSignOut} variant="outline" size="sm">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>

          {/* Sitemap Check */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Sitemap Check
                    {getStatusIcon(sitemapStatus)}
                  </CardTitle>
                  <CardDescription>Verifying sitemap.xml configuration</CardDescription>
                </div>
                <Button onClick={checkSitemap} variant="outline" size="sm">
                  Recheck
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Sitemap URL:</span>
                  <a 
                    href="/sitemap.xml" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {primaryDomain}/sitemap.xml
                  </a>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Primary Domain Usage:</span>
                  {getStatusBadge(sitemapStatus === 'success' ? 'success' : 'error')}
                </div>
                {sitemapStatus === 'error' && (
                  <p className="text-sm text-destructive mt-2">
                    ⚠️ Sitemap may contain incorrect domain references. Please verify.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Current Page Meta Tags */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Current Page Meta Tags</CardTitle>
              <CardDescription>Meta tags on this page</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {routeChecks.length > 0 && routeChecks[0].metaTags.map((tag, index) => (
                  <div key={index} className="border-b pb-3 last:border-0">
                    <div className="flex items-start justify-between mb-1">
                      <span className="font-medium text-sm">{tag.name}</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(tag.status)}
                        {getStatusBadge(tag.status)}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground break-all">{tag.content || 'Not set'}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tracking & Analytics */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Tracking & Analytics</CardTitle>
              <CardDescription>Current tracking tags and analytics configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Google Analytics 4:</span>
                  <Badge variant="default">G-P16Y239JYK</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Microsoft Clarity:</span>
                  <Badge variant="secondary">ty4cv1zb26</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">HubSpot:</span>
                  <Badge variant="secondary">43692900</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Load Strategy:</span>
                  <Badge variant="outline">Deferred (Performance Optimized)</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Domain Configuration */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Domain Configuration</CardTitle>
              <CardDescription>Primary domain settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Primary Domain:</span>
                  <Badge variant="default">{primaryDomain}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Robots.txt:</span>
                  <a 
                    href="/robots.txt" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm"
                  >
                    View robots.txt
                  </a>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Canonical Format:</span>
                  <code className="text-sm bg-muted px-2 py-1 rounded">https://ommni.io/path</code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SEO Checklist */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Checklist</CardTitle>
              <CardDescription>Essential SEO tasks for production</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <span className="font-medium">Submit sitemap to Google Search Console</span>
                    <p className="text-sm text-muted-foreground">URL: {primaryDomain}/sitemap.xml</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <span className="font-medium">Verify domain ownership in Google Search Console</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <span className="font-medium">Configure www → non-www redirect</span>
                    <p className="text-sm text-muted-foreground">Set ommni.io as primary in domain settings</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <span className="font-medium">Enable Google Analytics 4</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <span className="font-medium">Submit to Bing Webmaster Tools</span>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SEOHealthCheck;
